import React, { useRef, useState } from 'react';
import { Popup } from '@progress/kendo-react-popup';
import { SvgIcon } from '@progress/kendo-react-common';
import { exclamationCircleIcon } from '@progress/kendo-svg-icons';
import { useGlobalizationContext, useTranslation } from '@cs-cms/state-management';

const HELP_TEXT = {
  label: 'How search works',
  title: 'How search works',
  intro:
    'Search does not support partial metadata values. To find records by part of a value, enter the known text and an asterisk (*).',
  position: 'The position of the asterisk matters.',
  startMatch: 'matches the start of a value',
  endMatch: 'matches the end of a value',
  containsMatch: 'matches text anywhere in the value'
} as const;

type HelpTextKey = keyof typeof HELP_TEXT;

const HELP_TOOLTIP_ID = 'global-search-help';

export const GlobalSearchHelp: React.FC = () => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const [isPointerOver, setIsPointerOver] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const isOpen = isPointerOver || isFocused;

  const readHelpText = (suffix: HelpTextKey): string => {
    const messageKey = `custom.topBar.globalSearchHelp.${suffix}`;
    const message = translate(messageKey);
    if (message === messageKey) {
      return HELP_TEXT[suffix];
    }

    return message;
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={`globalSearchHelpButton${isOpen ? ' globalSearchHelpButton--open' : ''}`}
        aria-label={readHelpText('label')}
        aria-describedby={isOpen ? HELP_TOOLTIP_ID : undefined}
        onMouseEnter={(): void => {
          setIsPointerOver(true);
        }}
        onMouseLeave={(): void => {
          setIsPointerOver(false);
        }}
        onFocus={(): void => {
          setIsFocused(true);
        }}
        onBlur={(): void => {
          setIsFocused(false);
        }}
        onMouseDown={(event: React.MouseEvent<HTMLButtonElement>): void => {
          event.preventDefault();
        }}
      >
        <SvgIcon icon={exclamationCircleIcon} className="globalSearchHelpIcon" aria-hidden={true} />
      </button>
      {isOpen && anchorRef.current ? (
        <Popup
          anchor={anchorRef.current}
          show={true}
          animate={false}
          popupClass="globalSearchHelpPopup"
          anchorAlign={{ horizontal: 'right', vertical: 'bottom' }}
          popupAlign={{ horizontal: 'right', vertical: 'top' }}
          collision={{ horizontal: 'fit', vertical: 'flip' }}
        >
          <div id={HELP_TOOLTIP_ID} className="globalSearchHelp" role="tooltip">
            <p className="globalSearchHelpTitle">{readHelpText('title')}</p>
            <p className="globalSearchHelpText">{readHelpText('intro')}</p>
            <p className="globalSearchHelpText">{readHelpText('position')}</p>
            <ul className="globalSearchHelpExamples">
              <li className="globalSearchHelpExample">
                <code>Invoice*</code>
                <span>{readHelpText('startMatch')}</span>
              </li>
              <li className="globalSearchHelpExample">
                <code>*Report</code>
                <span>{readHelpText('endMatch')}</span>
              </li>
              <li className="globalSearchHelpExample">
                <code>*voice*</code>
                <span>{readHelpText('containsMatch')}</span>
              </li>
            </ul>
          </div>
        </Popup>
      ) : null}
    </>
  );
};
